import { ASSET_DIAGNOSTIC_NON_TERMINAL_STATUSES } from '@evimo/types/constants/asset-diagnostic';
import {
  OCPP_HEARTBEAT_INTERVAL_MAX_SECONDS,
  OCPP_HEARTBEAT_INTERVAL_MIN_SECONDS,
} from '~/src/constants/env';
import type {
  AssetCommandConsoleEntry,
  AssetCommandFeedbackKey,
  AssetCommandOutcome,
  AssetCommandRequest,
  AssetGetConfigurationKey,
  AssetOcppCommandAction,
  AssetResetType,
} from './types';

export { ASSET_DIAGNOSTIC_NON_TERMINAL_STATUSES };

export {
  ASSET_COMMAND_DEFINITIONS,
  GET_CONFIGURATION_KEYS,
  OCPP_COMMAND_CATEGORIES,
  TRIGGER_MESSAGES,
  commandsForCategory,
  findCommandDefinition,
} from './command-registry';

/** Re-export for Update Heartbeat form (values from VITE_* env). */
export {
  OCPP_HEARTBEAT_INTERVAL_MAX_SECONDS,
  OCPP_HEARTBEAT_INTERVAL_MIN_SECONDS,
};

export function shouldShowAssetCommands(input: {
  externalCsmsEnabled: boolean;
  canUpdateAsset: boolean;
}): boolean {
  return input.externalCsmsEnabled === true && input.canUpdateAsset === true;
}

export function assetCommandResultKey(input: {
  outcome?: AssetCommandOutcome;
  status?: number;
  reason?: string;
}): AssetCommandFeedbackKey {
  if (input.status === 409 && input.reason === 'csms_not_configured') {
    return 'csmsNotConfigured';
  }
  if (input.status === 409 && input.reason === 'charger_offline') {
    return 'offline';
  }
  if (input.status === 409 && input.reason === 'diagnostic_active') {
    return 'diagnosticActive';
  }
  if (input.status === 400 && input.reason === 'invalid_command') {
    return 'error';
  }
  if (input.status === 504 && input.reason === 'charger_timeout') {
    return 'chargerTimeout';
  }
  if (input.status === 504) {
    return 'ownerTimeout';
  }
  if (
    input.outcome === 'accepted' ||
    input.outcome === 'rejected' ||
    input.outcome === 'notSupported' ||
    input.outcome === 'callError'
  ) {
    return input.outcome;
  }
  return 'error';
}

export function resetTypeLabelKey(type: AssetResetType): string {
  return type === 'Hard' ? 'detail.commands.hard' : 'detail.commands.soft';
}

export function requestPayloadFor(
  request: AssetCommandRequest,
): Record<string, unknown> {
  switch (request.action) {
    case 'Reset':
      return { type: request.type };
    case 'GetConfiguration':
      return { key: request.keys };
    case 'TriggerMessage':
      return { requestedMessage: request.requestedMessage };
    case 'ChangeConfiguration':
      return { key: request.key, value: request.value };
    case 'GetDiagnostics': {
      // Never include location / upload token in the console CALL payload.
      const payload: Record<string, unknown> = {};
      if (request.retries != null) {
        payload.retries = request.retries;
      }
      if (request.retryInterval != null) {
        payload.retryInterval = request.retryInterval;
      }
      if (request.startTime != null) {
        payload.startTime = request.startTime;
      }
      if (request.stopTime != null) {
        payload.stopTime = request.stopTime;
      }
      return payload;
    }
    default:
      return {};
  }
}

/** Strip upload capability / location fields before console display (ADR-0019). */
export function redactDiagnosticsSensitiveFields(
  payload: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (payload == null) {
    return undefined;
  }
  const {
    location: _location,
    locationUri: _locationUri,
    uploadToken: _uploadToken,
    rawUploadToken: _rawUploadToken,
    ...safe
  } = payload;
  return safe;
}

export function isAssetDiagnosticNonTerminal(status: string): boolean {
  return (ASSET_DIAGNOSTIC_NON_TERMINAL_STATUSES as readonly string[]).includes(
    status,
  );
}

export function formatConsoleTimestamp(at: Date): string {
  const hours = String(at.getHours()).padStart(2, '0');
  const minutes = String(at.getMinutes()).padStart(2, '0');
  const seconds = String(at.getSeconds()).padStart(2, '0');
  const ms = String(at.getMilliseconds()).padStart(3, '0');
  return `${hours}:${minutes}:${seconds}.${ms}`;
}

export function createConsoleId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function buildSendConsoleEntries(input: {
  action: AssetOcppCommandAction;
  requestPayload: Record<string, unknown>;
  at?: Date;
}): AssetCommandConsoleEntry[] {
  const at = input.at ?? new Date();
  return [
    {
      id: createConsoleId(),
      at,
      direction: 'status',
      kind: 'waiting',
      action: input.action,
      messageKey: 'detail.commands.console.waiting',
    },
    {
      id: createConsoleId(),
      at,
      direction: 'outbound',
      kind: 'send',
      action: input.action,
      messageKey: 'detail.commands.console.call',
      messageParams: { action: input.action },
      payload:
        input.action === 'GetDiagnostics'
          ? (redactDiagnosticsSensitiveFields(input.requestPayload) ?? {})
          : input.requestPayload,
    },
  ];
}

export function buildSuccessConsoleEntries(input: {
  action: AssetOcppCommandAction;
  uniqueId: string;
  outcome: AssetCommandOutcome;
  responsePayload?: Record<string, unknown>;
  listVersion?: number;
  errorCode?: string;
  errorDescription?: string;
  durationMs: number;
  at?: Date;
}): AssetCommandConsoleEntry[] {
  const at = input.at ?? new Date();
  const isError = input.outcome === 'callError';
  const completed = buildCompletedConsoleMessage(input);

  const completedEntry: AssetCommandConsoleEntry = {
    id: createConsoleId(),
    at,
    direction: 'status',
    kind: 'completed',
    action: input.action,
    uniqueId: input.uniqueId,
    durationMs: input.durationMs,
    outcome: input.outcome,
    messageKey: completed.messageKey,
    ...(completed.messageParams != null
      ? { messageParams: completed.messageParams }
      : {}),
  };

  const rawPayload =
    input.responsePayload ??
    (isError
      ? {
          errorCode: input.errorCode,
          errorDescription: input.errorDescription,
        }
      : undefined);
  const received: AssetCommandConsoleEntry = {
    id: createConsoleId(),
    at,
    direction: 'inbound',
    kind: isError ? 'callError' : 'callResult',
    action: input.action,
    uniqueId: input.uniqueId,
    messageKey: isError
      ? 'detail.commands.console.callError'
      : 'detail.commands.console.callResult',
    payload:
      input.action === 'GetDiagnostics'
        ? redactDiagnosticsSensitiveFields(rawPayload)
        : rawPayload,
  };

  return [completedEntry, received];
}

function buildCompletedConsoleMessage(input: {
  action: AssetOcppCommandAction;
  outcome: AssetCommandOutcome;
  responsePayload?: Record<string, unknown>;
  listVersion?: number;
  errorCode?: string;
}): {
  messageKey: string;
  messageParams?: Record<string, string | number>;
} {
  if (input.outcome === 'rejected') {
    return { messageKey: 'detail.commands.console.completedRejected' };
  }
  if (input.outcome === 'notSupported') {
    return { messageKey: 'detail.commands.console.completedNotSupported' };
  }
  if (input.outcome === 'callError') {
    return input.errorCode
      ? {
          messageKey: 'detail.commands.console.completedFailedWithCode',
          messageParams: { errorCode: input.errorCode },
        }
      : { messageKey: 'detail.commands.console.completedFailed' };
  }
  if (
    input.action === 'GetLocalListVersion' &&
    input.listVersion !== undefined
  ) {
    return input.listVersion === -1
      ? { messageKey: 'detail.commands.console.completedLocalListEmpty' }
      : {
          messageKey: 'detail.commands.console.completedLocalListVersion',
          messageParams: { version: input.listVersion },
        };
  }
  if (input.action === 'ChangeConfiguration') {
    const status =
      input.responsePayload && typeof input.responsePayload.status === 'string'
        ? input.responsePayload.status
        : 'Accepted';
    return status === 'RebootRequired'
      ? { messageKey: 'detail.commands.console.completedRebootRequired' }
      : { messageKey: 'detail.commands.console.completedHeartbeatUpdated' };
  }
  if (input.action === 'GetConfiguration') {
    return summarizeGetConfigurationMessage(input.responsePayload);
  }
  return { messageKey: 'detail.commands.console.completedAccepted' };
}

function summarizeGetConfigurationMessage(
  responsePayload: Record<string, unknown> | undefined,
): {
  messageKey: string;
  messageParams?: Record<string, string | number>;
} {
  const keys = Array.isArray(responsePayload?.configurationKey)
    ? responsePayload.configurationKey
    : [];
  const unknown = Array.isArray(responsePayload?.unknownKey)
    ? responsePayload.unknownKey
    : [];
  const parts: string[] = [];
  for (const entry of keys) {
    if (
      entry != null &&
      typeof entry === 'object' &&
      'key' in entry &&
      'value' in entry
    ) {
      const row = entry as { key: unknown; value: unknown };
      parts.push(`${String(row.key)}=${String(row.value)}`);
    }
  }
  if (unknown.length > 0) {
    parts.push(`unknown:${unknown.map(String).join(',')}`);
  }
  if (parts.length === 0) {
    return {
      messageKey: 'detail.commands.console.completedConfigurationEmpty',
    };
  }
  return {
    messageKey: 'detail.commands.console.completedConfiguration',
    messageParams: { summary: parts.join('; ') },
  };
}

export function buildInfraErrorConsoleEntry(input: {
  action: AssetOcppCommandAction;
  messageKey: string;
  messageParams?: Record<string, string | number>;
  kind?: 'timeout' | 'infraError';
  durationMs?: number;
  at?: Date;
}): AssetCommandConsoleEntry {
  return {
    id: createConsoleId(),
    at: input.at ?? new Date(),
    direction: 'status',
    kind: input.kind ?? 'infraError',
    action: input.action,
    durationMs: input.durationMs,
    messageKey: input.messageKey,
    ...(input.messageParams != null
      ? { messageParams: input.messageParams }
      : {}),
  };
}

export function parseHeartbeatIntervalFromConfiguration(
  responsePayload: Record<string, unknown> | undefined,
): string | null {
  const keys = Array.isArray(responsePayload?.configurationKey)
    ? responsePayload.configurationKey
    : [];
  for (const entry of keys) {
    if (
      entry != null &&
      typeof entry === 'object' &&
      'key' in entry &&
      (entry as { key: unknown }).key === 'HeartbeatInterval' &&
      'value' in entry
    ) {
      const value = (entry as { value: unknown }).value;
      return value == null ? null : String(value);
    }
  }
  return null;
}

export function isPositiveIntegerString(value: string): boolean {
  return /^[1-9]\d*$/.test(value.trim());
}

export const OCPP_HEARTBEAT_INTERVAL_RANGE_MESSAGE = `Heartbeat interval must be between ${OCPP_HEARTBEAT_INTERVAL_MIN_SECONDS} and ${OCPP_HEARTBEAT_INTERVAL_MAX_SECONDS} seconds`;

export function isValidOcppHeartbeatIntervalValue(
  value: unknown,
): value is string {
  if (typeof value !== 'string' || !/^[1-9]\d*$/.test(value)) {
    return false;
  }
  const seconds = Number(value);
  return (
    Number.isInteger(seconds) &&
    seconds >= OCPP_HEARTBEAT_INTERVAL_MIN_SECONDS &&
    seconds <= OCPP_HEARTBEAT_INTERVAL_MAX_SECONDS
  );
}

export function configurationKeyLabelKey(
  key: AssetGetConfigurationKey,
): string {
  return `detail.commands.getConfiguration.keys.${key}`;
}
